<template>
  <section class="relative flex flex-col w-full bg-white lg:block">
    <div class="w-full lg:w-[calc(100%-230px)] lg:min-h-screen p-4">
      <header class="text-center lg:text-left">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-zinc-950 md:text-3xl">{{ rudiment.title }}</h1>
        </div>      
      </header>
      
      <div class="player-view flex items-center justify-center mt-8 md:mt-16 lg:mt-24 mb-8 md:mb-8 lg:mb-12">
        <Notation 
        :id="parseInt(route.params.slug)" 
        :data="rudiment" 
        />
      </div>

      <div class="hidden">
        <img
        v-if="rudiment?.image"
        :src="'https://media.drumrudiments.app/'+rudiment.image.id+'/'+rudiment.image.filename_download"
        :height="rudiment.image.height"
        :width="rudiment.image.width"
        :alt="'How to play ' + rudiment.title + ': drum rudiment notation'"
        loading="lazy"
        class="rounded-xl"
        />
      </div>

      <Player :mini="true" :data="rudiment" />
      
      <!-- Prose content -->
      <div v-if="rudiment.content" class="prose max-w-[700px] mx-auto">
        <div v-html="rudiment.content"></div>
      </div>
    </div>

    <!-- Sidebar -->
    <aside 
    v-if="routeCollection?.length && rudiment?.collections_rudiments?.length"
    class="relative w-full overflow-hidden lg:absolute lg:top-0 lg:right-0 lg:w-[230px] lg:h-full bg-white border-t lg:border-l">
      <div class="w-full h-full lg:overflow-y-auto no-scrollbar">
        
        <div class="px-4 pt-4 pb-4 lg:pb-2">
          <NuxtLink 
          v-if="rudiment?.collections_rudiments[0].collections?.slug"
          :to="'/collection/'+rudiment?.collections_rudiments[0].collections?.slug" class="">
            <h2 class="text-xl font-semibold lg:text-sm/5">
              <span class="underline">{{ rudiment.collections_rudiments[0].collections?.title }}</span> collection
            </h2>
          </NuxtLink>
        </div>

        <div class="flex gap-4 px-4 pb-4 overflow-x-auto lg:flex-col lg:overflow-x-visible lg:gap-0 no-scrollbar">

          <template v-for="(item, index) in routeCollection">
            <div
            v-if="item?.rudiments?.slug"
            :class="[ 
              item.rudiments.slug === route.params.slug ? 'bg-zinc-950' : 'bg-gray-200',
              { 'lg:mt-2' : index === 0 }
            ]"
            class="p-1 rounded flex-shrink-0 flex items-center justify-center mb-2 w-[160px] md:w-[210px] lg:w-full">
              <NuxtLink :to="`/rudiment/${item.rudiments.slug}`" class="p-2 w-full items-center justify-center">
                <img
                v-if="item.rudiments?.image"
                :src="'https://media.drumrudiments.app/'+item.rudiments.image.id+'/'+item.rudiments.image.filename_download"
                :height="item.rudiments.image.height"
                :width="item.rudiments.image.width"
                :alt="item.rudiments.title + ' drum rudiment notation'"
                classes="rounded-xl"
                />
                <h2 
                :class="[ 
                  'text-lg font-semibold sm:text-sm/5 mt-2',
                  item.rudiments.slug === route.params.slug ? 'text-white' : 'text-zinc-950'
                ]"
                >
                  {{ item.rudiments.title }}
                </h2>
              </NuxtLink>
            </div>
          </template>
        </div>
      </div>
    </aside>
  </section>
</template>

<script setup>
const { $seo } = useNuxtApp()
const route = useRoute()
const slug = route.params.slug 
const supabase = useSupabaseClient()
const config = useRuntimeConfig()
const optionsStore = useOptionsStore()

const routeCollection = ref([])

// First query with useAsyncData
const { data: rudiment } = await useAsyncData(
  'rudiment-logged-out-'+slug, 
  async () => {
    const { data } = await supabase
      .from('rudiments')
      .select(`
        *,
        seo(
          *,
          meta_image(
            id,
            filename_download,
            description,
            width,
            height
          )
        ),
        collections_rudiments(
          collections(
            title,
            slug
          )
        ),
        rudiments_rudiment_categories(
          rudiment_categories(
            title,
            slug
          )
        ),
        image(
          id,
          filename_download,
          description,
          width,
          height
        )
      `)
      .eq('slug', slug)
      .in('status', config.public.postStatus.split(','))
      .maybeSingle() 
    
    if (!data) {
      throw createError({ statusCode: 404 })
    }

    return data
  }
)

// Second query depending on first one
const { data: collection } = await useAsyncData(
  'collection-data-'+slug,
  async () => {
    if (!rudiment.value) return null

    const collectionSlug = rudiment.value?.collections_rudiments?.[0]?.collections?.slug
    if (!collectionSlug) return null

    const { data: collectionRudiments } = await supabase
      .from('collections')
      .select(`
        collections_rudiments(
          rudiments_sort,
          rudiments(
            title, 
            slug,
            image(
              id,
              filename_download,
              description,
              width,
              height
            ),
            rudiments_rudiment_categories(
              rudiment_categories(
                title,
                slug
              )
            )  
          )
        )
      `)
      .eq('slug', collectionSlug)
      .order('rudiments_sort', { referencedTable: 'collections_rudiments', ascending: true })
      .maybeSingle()

    return collectionRudiments
  }
)

routeCollection.value = collection.value?.collections_rudiments

// if (rudiment.value && process.client) {
//   let breadcrumbs = optionsStore.breadcrumbs
//   if (breadcrumbs[breadcrumbs.length - 1].post) {
//     breadcrumbs.pop()
//   }
//   breadcrumbs.push({ 
//     title: rudiment.value.title, 
//     to: `/rudiment/${slug}`, 
//     post: true 
//   })
//   optionsStore.setBreadcrumbs(breadcrumbs)
// }

// Handle SEO
const seo = computed(() => {
  if (!rudiment.value) return null

  if (rudiment.value?.seo?.meta_title && rudiment.value?.seo?.meta_description && rudiment.value?.seo?.meta_description.length > 10) {
    return $seo(rudiment.value.seo, route.fullPath)
  }

  if (rudiment.value?.seo?.meta_title) {
    return $seo({
      meta_title: rudiment.value.seo.meta_title,
      meta_description: `Learn how to master the ${rudiment.value.title} drum rudiment with our step-by-step guide. Get expert tips and notation. Sign up to play along and save your progress!`,
      meta_image: rudiment.value.image
    }, route.fullPath)
  }

  return $seo({
    meta_title: `How to Play the ${rudiment.value.title}: Play Along`,
    meta_description: `Learn how to master the ${rudiment.value.title} drum rudiment with our step-by-step guide. Get expert tips and notation. Sign up to play along and save your progress!`,
    meta_image: rudiment.value.image
  }, route.fullPath)
})

useHead(seo)

const imgSchema = ref(null)
if (rudiment.value?.image) {
  imgSchema.value = {
    "@context": "http://schema.org",
    "@type": "ImageObject",
    "contentUrl": `https://media.drumrudiments.app/${rudiment.value.image.id}/${rudiment.value.image.filename_download}`,
    "name": `${rudiment.value.title} drum rudiment notation`,
    "url": `https://media.drumrudiments.app/rudiment/${rudiment.value.slug}`,
    "description": `How to play ${rudiment.value.title} | Drum rudiment notation`,
    "width": rudiment.value.image.width,
    "height": rudiment.value.image.height,
    "license": "https://creativecommons.org/licenses/by-sa/4.0/",
    "fileFormat": "image/png",
    "datePublished": rudiment.value.date_created
  }
}

useSchemaOrg([
  imgSchema.value
])

</script>